import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { mergeRefs } from 'use-callback-ref';

import Badge from '@atlaskit/badge/badge-new';
import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

import { useSuggestionsFeedback } from '../useSuggestionsFeedback.ts';

import { SuggestionsFeedbackCollectorLoader } from './SuggestionsFeedbackCollectorLoader.tsx';

type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const i18n = defineMessages({
	moreActions: {
		id: 'suggestions-panel.header.more-actions',
		defaultMessage: 'More actions',
		description:
			'Accessible label and tooltip for the menu containing actions for all AI suggestions',
	},
	// Replaces the retired `header.review-again` ("Review again") message rather than
	// rewording it, so any in-flight translations of the old string can just be dropped.
	review: {
		id: 'suggestions-panel.header.review',
		defaultMessage: 'Review',
		description: 'Button to review the document for AI suggestions',
	},
	giveFeedback: {
		id: 'suggestions-panel.header.give-feedback',
		defaultMessage: 'Give feedback',
		description: 'Menu item to share general feedback about suggestions',
	},
});

const useSuggestionsHeaderState = () => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const state = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		({ aiSuggestionsState }: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			count: aiSuggestionsState?.orderedSuggestionIds.length ?? 0,
			isThinking: aiSuggestionsState?.thinkingStatus === 'thinking',
		}),
	);
	return { ...state, api: fullPageEditorPresetAPI };
};

export const SuggestionsCount = (): React.JSX.Element | null => {
	const { count } = useSuggestionsHeaderState();
	return count > 0 ? <Badge max={false}>{count}</Badge> : null;
};

export const SuggestionsHeaderActions = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { isThinking, api } = useSuggestionsHeaderState();
	const actions = api?.aiSuggestions?.actions;
	const feedback = useSuggestionsFeedback();

	return (
		<>
			<DropdownMenu
				placement="bottom-end"
				trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
					<IconButton
						{...triggerProps}
						ref={mergeRefs([triggerRef, feedback.triggerRef])}
						icon={ShowMoreHorizontalIcon}
						label={formatMessage(i18n.moreActions)}
						isTooltipDisabled={false}
						appearance="subtle"
						spacing="default"
					/>
				)}
			>
				<DropdownItemGroup>
					<DropdownItem
						elemBefore={<FeedbackIcon label="" />}
						onClick={feedback.open}
						returnFocusRef={feedback.triggerRef}
					>
						{formatMessage(i18n.giveFeedback)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			<IconButton
				icon={TextReviewIcon}
				label={formatMessage(i18n.review)}
				isDisabled={isThinking}
				isTooltipDisabled={false}
				appearance="subtle"
				spacing="default"
				onClick={() =>
					actions?.setThinkingState('thinking', {
						entryPoint: AiSuggestionsEntryPointValue.SUGGESTIONS_PANEL_HEADER,
					})
				}
			/>
			{feedback.isOpen && (
				<SuggestionsFeedbackCollectorLoader
					onClose={feedback.close}
					shouldReturnFocusRef={feedback.triggerRef}
				/>
			)}
		</>
	);
};
